import { FC, useEffect, useState } from 'react';
import { Button, Card, Divider, RadixTable } from '@features/ui';
import {
  TReportActiveUsersByProductData,
  TReportActiveUsersByProductPayload,
} from '@src/types';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { useReportTableColumns } from '@hooks/useReportTableColumns';
import { useProducts } from '@src/hooks/useProducts';
import {
  exportActiveUsersByProducts,
  getActiveUsersByProducts,
} from '@src/services/reports';
import { toast } from 'react-toastify';
import { downloadFile } from '@src/lib/utils';
import { canAccess } from '@src/utils/auth';
import { UserRolePermission } from '@src/types/settings';
import { useAuthStore } from '@src/stores/authStore';

import { ReportFilteringSettings } from '../ReportFilteringSettings';

interface TooltipProps {
  active?: boolean;
  payload?: Array<{
    value: number;
    payload: TReportActiveUsersByProductData;
  }>;
}

const CustomTooltip: FC<TooltipProps> = ({ active, payload }) => {
  if (active && payload && payload.length) {
    return (
      <div
        className={
          'bg-card-stat p-2.5 rounded-1 text-xs border-1 border-[#3D3D3D] rounded-[8px] flex flex-col items-center min-w-[135px] gap-y-1'
        }
        style={{
          boxShadow: '0px 7px 13px 0px #B6D72C14',
        }}
      >
        <p className="text-[#B6D72C] text-xl font-semibold">
          {payload[0]?.value}
        </p>
        <p className="text-[#999999] text-[10px]">
          {payload[0]?.payload?.product}
        </p>
        <p className="text-white text-xs">Active Users: {payload[0]?.value}</p>
      </div>
    );
  }

  return null;
};

export const ActiveUsersByProducts: FC = () => {
  const { auth } = useAuthStore();
  const [selectedProducts, setSelectedProducts] = useState<string[]>([]);
  const [data, setData] = useState<TReportActiveUsersByProductData[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const { products } = useProducts();

  // Use custom hook for table columns
  const { columns, tableData } =
    useReportTableColumns<TReportActiveUsersByProductData>({
      data,
      getAccessField: (item) => item.product,
      getMainDataField: (item) => item.active_users || 0,
      columnTitleFormatter: () => 'ACTIVE USERS',
      includeTotalsRow: true,
      customColumns: [
        {
          title: 'ACTIVE USERS',
          className: 'text-right',
          render: (item) => (
            <p className="text-white">{item?.active_users ?? 0}</p>
          ),
        },
      ],
    });

  const handleProductsChange = (values: string[]) => {
    setSelectedProducts(values);
  };

  const handleRunReport = async () => {
    setIsLoading(true);
    try {
      const payload: TReportActiveUsersByProductPayload = {
        products: selectedProducts.map((p) => Number(p)),
      };

      const response = await getActiveUsersByProducts(payload);

      setData(response.data);
      toast.success('Report generated successfully!');
    } catch (error) {
      console.error('Error fetching report:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleExportReport = async () => {
    try {
      const payload: TReportActiveUsersByProductPayload = {
        products: selectedProducts.map((p) => Number(p)),
      };

      const response = await exportActiveUsersByProducts(payload);

      downloadFile(response.data, 'active-users-by-products');
      toast.success('Report exported successfully!');
    } catch (error) {
      console.error('Error exporting report:', error);
    }
  };

  useEffect(() => {
    handleRunReport();
  }, []);

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold text-white">
        Active Users by Products
      </h1>

      <Card className="p-6">
        <ReportFilteringSettings
          showProductsFilter
          isLoading={isLoading}
          productOptions={products}
          selectedProducts={selectedProducts}
          showGroupByFilter={false}
          showPeriodFilter={false}
          onProductsChange={handleProductsChange}
          onRunReport={handleRunReport}
        />
      </Card>

      <Card className="p-6">
        <div className="space-y-4">
          <ResponsiveContainer height={400} width="100%">
            <BarChart data={data}>
              <CartesianGrid stroke="#333" strokeDasharray="3 3" />
              <XAxis
                dataKey="product"
                stroke="#999"
                style={{ fontSize: '12px' }}
              />
              <YAxis
                stroke="#999"
                style={{ fontSize: '12px' }}
                tickFormatter={(value) => value.toLocaleString()}
              />
              <Tooltip
                content={<CustomTooltip />}
                cursor={{ fill: 'transparent' }}
              />
              <Legend iconType="rect" wrapperStyle={{ fontSize: '14px' }} />
              <Bar
                barSize={60}
                dataKey="active_users"
                fill="#B6D72C"
                name="Active Users"
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <Card className="p-6">
        <div className="space-y-4">
          <RadixTable columns={columns} data={tableData} />
          <Divider className="my-4" />
          {canAccess(
            UserRolePermission.EXPORT_REPORTS,
            auth?.user?.permissions
          ) && (
            <div className="flex justify-end">
              <Button
                className="text-[#B6D72C] border-[#B6D72C] hover:bg-[#B6D72C] hover:text-black"
                variant="outline"
                onClick={handleExportReport}
              >
                Download Excel
              </Button>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};
